import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Play, TrendingUp, Star, ChevronRight, Heart, Check, Sparkles, Tv } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { getTrending, getImageUrl, addMovie, getUserMovies, getTvdbLogo, getContinueWatching, getPersonalizedRecommendations, updateMovie } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
// axios no longer needed here (API client handles auth/base URL)

const HeroSection = ({ item }) => {
  if (!item) return null;
  
  const title = item.title || item.name;
  const backdrop = getImageUrl(item.backdrop_path, 'w1280');
  const isMovie = item.media_type === 'movie';
  
  return (
    <section className="relative h-[70vh] md:h-[80vh] overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0">
        {backdrop && (
          <img 
            src={backdrop} 
            alt={title}
            className="w-full h-full object-cover"
          />
        )}
        {/* Lighter Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/40 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#050505]/70 via-transparent to-transparent" />
      </div>

      {/* Content */}
      <div className="relative h-full flex flex-col justify-end p-6 md:p-12 lg:p-16 max-w-3xl">
        <span className="text-xs uppercase tracking-[0.2em] text-indigo-400 font-medium mb-3">
          {isMovie ? 'Trending Film' : 'Trending Serie'}
        </span>
        
        <h1 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold tracking-tight text-white mb-4">
          {title}
        </h1>
        
        <div className="flex items-center gap-4 text-sm text-gray-300 mb-6">
          <span className="flex items-center gap-1">
            <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
            {item.vote_average?.toFixed(1)}
          </span>
          <span>{isMovie ? item.release_date?.slice(0, 4) : item.first_air_date?.slice(0, 4)}</span>
        </div>
        
        <p className="text-gray-300 text-base md:text-lg line-clamp-3 mb-8 max-w-xl">
          {item.overview}
        </p>
        
        <div className="flex flex-wrap gap-4">
          <Link to={`/${isMovie ? 'movie' : 'tv'}/${item.id}`}>
            <Button 
              data-testid="hero-details-btn"
              className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-full px-8 py-6 font-semibold shadow-[0_0_20px_rgba(99,102,241,0.4)] transition-all hover:scale-105"
            >
              <Play className="w-5 h-5 mr-2" />
              Bekijk Details
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};

const ContinueWatchingCard = ({ series }) => {
  const poster = getImageUrl(series.poster_path, 'w342');
  const progress = series.progress_percent || 0;
  const episodesLeft = series.total_episodes - (series.episodes_watched || 0);
  const [logoUrl, setLogoUrl] = useState(series.logo_path);
  const [logoError, setLogoError] = useState(false);

  // Fetch TVDB logo if not available in the database
  useEffect(() => {
    const fetchLogo = async () => {
      if (!series.logo_path && series.title) {
        try {
          const logo = await getTvdbLogo(series.title);
          if (logo) {
            setLogoUrl(logo);
          }
        } catch (e) {
          console.log('Could not fetch logo for', series.title);
        }
      }
    };
    fetchLogo();
  }, [series.logo_path, series.title]);
  
  return (
    <Link 
      to={`/tv/${series.tmdb_id}`}
      className="group flex-shrink-0 w-72 rounded-xl overflow-hidden bg-white/5 border border-white/5 hover:border-indigo-500/30 transition-all"
    >
      <div className="flex h-32">
        {/* Poster */}
        <div className="w-24 flex-shrink-0">
          {poster ? (
            <img src={poster} alt={series.title} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gray-800">
              <Tv className="w-6 h-6 text-gray-600" />
            </div>
          )}
        </div>
        
        {/* Info */}
        <div className="flex-1 p-3 flex flex-col justify-between">
          <div>
            {/* Logo or Title */}
            {logoUrl && !logoError ? (
              <img 
                src={logoUrl}
                alt={series.title}
                className="h-8 max-w-full object-contain mb-1"
                onError={() => setLogoError(true)}
              />
            ) : (
              <h3 className="font-semibold text-sm line-clamp-1">{series.title}</h3>
            )}
            <p className="text-xs text-gray-400 mt-1">
              Nog {episodesLeft} aflevering{episodesLeft !== 1 ? 'en' : ''}
            </p>
          </div>
          
          {/* Progress */}
          <div>
            <div className="flex justify-between text-xs mb-1">
              <span className="text-gray-500">{series.episodes_watched}/{series.total_episodes}</span>
              <span className="text-indigo-400">{progress}%</span>
            </div>
            <Progress value={progress} className="h-1.5" />
          </div>
        </div>
      </div>
    </Link>
  );
};

const MediaCard = ({ item, index, userMovies, onQuickAction }) => {
  const title = item.title || item.name;
  const poster = getImageUrl(item.poster_path, 'w342');
  const isMovie = item.media_type === 'movie' || item.title;
  
  const userEntry = userMovies?.find(m => m.tmdb_id === item.id);
  const isInWatchlist = userEntry?.status === 'watchlist';
  const isWatched = userEntry?.status === 'watched';
  
  const handleWatchlistClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isMovie) return;
    onQuickAction(item, 'watchlist');
  };

  const handleWatchedClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isMovie) return;
    onQuickAction(item, 'watched');
  };
  
  return (
    <Link 
      to={`/${isMovie ? 'movie' : 'tv'}/${item.id}`}
      data-testid={`media-card-${item.id}`}
      className="group relative aspect-[2/3] rounded-lg overflow-hidden bg-gray-900 transition-all duration-300 hover:ring-2 hover:ring-indigo-500 hover:scale-[1.02] animate-fade-in"
      style={{ animationDelay: `${index * 50}ms` }}
    >
      {poster ? (
        <img 
          src={poster} 
          alt={title}
          className="w-full h-full object-cover"
          loading="lazy"
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center bg-gray-800">
          <span className="text-gray-500 text-sm text-center px-2">{title}</span>
        </div>
      )}
      
      {/* Quick Action Buttons (Glass Overlay) - Only for movies */}
      {isMovie && (
        <div className="absolute top-2 right-2 flex flex-col gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
          {!isWatched && (
            <button
              onClick={handleWatchlistClick}
              className={`w-9 h-9 rounded-full backdrop-blur-md flex items-center justify-center transition-all ${
                isInWatchlist 
                  ? 'bg-pink-500/80 text-white' 
                  : 'bg-black/50 text-white/70 hover:bg-black/70 hover:text-pink-400'
              }`}
              title="Toevoegen aan watchlist"
            >
              <Heart className={`w-4 h-4 ${isInWatchlist ? 'fill-current' : ''}`} />
            </button>
          )}
          <button
            onClick={handleWatchedClick}
            className={`w-9 h-9 rounded-full backdrop-blur-md flex items-center justify-center transition-all ${
              isWatched 
                ? 'bg-green-500/80 text-white' 
                : 'bg-black/50 text-white/70 hover:bg-black/70 hover:text-green-400'
            }`}
            title="Markeer als gekeken"
          >
            <Check className="w-4 h-4" />
          </button>
        </div>
      )}
      
      {/* Status Badge */}
      {isWatched && (
        <div className="absolute top-2 left-2 bg-green-500 rounded-full p-1.5">
          <Check className="w-3 h-3 text-white" />
        </div>
      )}
      {isInWatchlist && !isWatched && (
        <div className="absolute top-2 left-2 bg-pink-500 rounded-full p-1.5">
          <Heart className="w-3 h-3 text-white fill-current" />
        </div>
      )}
      
      {/* Hover Overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
        <div className="absolute bottom-0 left-0 right-0 p-4">
          <h3 className="text-white font-semibold text-sm mb-1 line-clamp-2">{title}</h3>
          <div className="flex items-center gap-2 text-xs text-gray-300">
            <Star className="w-3 h-3 text-yellow-500 fill-yellow-500" />
            <span>{item.vote_average?.toFixed(1)}</span>
          </div>
        </div>
      </div>
    </Link>
  );
};

const MediaRow = ({ title, items, icon: Icon, viewAllLink, userMovies, onQuickAction }) => {
  if (!items || items.length === 0) return null;
  
  return (
    <section className="py-8">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          {Icon && <Icon className="w-5 h-5 text-indigo-400" />}
          <h2 className="text-xl md:text-2xl font-display font-bold text-white">{title}</h2>
        </div>
        {viewAllLink && (
          <Link 
            to={viewAllLink}
            className="flex items-center gap-1 text-sm text-gray-400 hover:text-white transition-colors"
          >
            Bekijk alles
            <ChevronRight className="w-4 h-4" />
          </Link>
        )}
      </div>
      
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
        {items.slice(0, 12).map((item, index) => (
          <MediaCard 
            key={item.id} 
            item={item} 
            index={index} 
            userMovies={userMovies}
            onQuickAction={onQuickAction}
          />
        ))}
      </div>
    </section>
  );
};

export const HomePage = () => {
  const { isAuthenticated, token } = useAuth();
  const [trending, setTrending] = useState([]);
  const [trendingMovies, setTrendingMovies] = useState([]);
  const [trendingTv, setTrendingTv] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [continueWatching, setContinueWatching] = useState([]);
  const [userMovies, setUserMovies] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchUserMovies = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const data = await getUserMovies();
      setUserMovies(data.movies || []);
    } catch (error) {
      console.error('Error fetching user movies:', error);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [allTrending, moviesTrending, tvTrending] = await Promise.all([
          getTrending('all', 'day'),
          getTrending('movie', 'week'),
          getTrending('tv', 'week')
        ]);
        
        setTrending(allTrending.results || []);
        setTrendingMovies(moviesTrending.results || []);
        setTrendingTv(tvTrending.results || []);

        // Fetch personalized data if authenticated
        if (isAuthenticated && token) {
          try {
            // Fetch continue watching
            const continueResponse = await getContinueWatching();
            setContinueWatching(continueResponse.series || []);
          } catch (e) {
            console.log('No continue watching data');
          }

          try {
            // Fetch personalized recommendations (already filtered server-side)
            const recResponse = await getPersonalizedRecommendations();
            setRecommendations(recResponse.results || []);
          } catch (e) {
            console.log('No personalized recommendations yet');
          }
        }

        await fetchUserMovies();
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [isAuthenticated, token, fetchUserMovies]);

  const handleQuickAction = async (item, action) => {
    if (!isAuthenticated) {
      toast.error('Log in om films toe te voegen');
      return;
    }

    const existingEntry = userMovies.find(m => m.tmdb_id === item.id);

    try {
      if (action === 'watchlist') {
        if (existingEntry) {
          toast.info('Film staat al in je bibliotheek');
          return;
        }
        await addMovie({
          tmdb_id: item.id,
          title: item.title,
          poster_path: item.poster_path,
          backdrop_path: item.backdrop_path,
          status: 'watchlist',
          runtime: item.runtime || 0,
          genres: item.genre_ids?.map(id => String(id)) || []
        });
        toast.success('Toegevoegd aan watchlist');
      } else if (action === 'watched') {
        if (existingEntry) {
          // Update existing entry
          await updateMovie(existingEntry.id, { status: 'watched' });
        } else {
          await addMovie({
            tmdb_id: item.id,
            title: item.title,
            poster_path: item.poster_path,
            backdrop_path: item.backdrop_path,
            status: 'watched',
            runtime: item.runtime || 0,
            genres: item.genre_ids?.map(id => String(id)) || []
          });
        }
        toast.success('Gemarkeerd als gekeken');
      }
      
      await fetchUserMovies();
    } catch (error) {
      console.error('Quick action error:', error);
      toast.error('Kon actie niet uitvoeren');
    }
  };

  const heroItem = trending[0];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="home-page" className="min-h-screen">
      {/* Hero Section */}
      <HeroSection item={heroItem} />

      {/* Content Sections */}
      <div className="px-4 md:px-8 lg:px-12 space-y-4 relative z-10 pt-6 sm:pt-8 md:pt-4 md:-mt-12 lg:-mt-16">
        {/* Continue Watching Section */}
        {isAuthenticated && continueWatching.length > 0 && (
          <section className="py-8">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <Play className="w-5 h-5 text-green-400" />
                <h2 className="text-xl md:text-2xl font-display font-bold text-white">Verder kijken</h2>
              </div>
              <Link 
                to="/series"
                className="flex items-center gap-1 text-sm text-gray-400 hover:text-white transition-colors"
              >
                Alle series
                <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
            
            <div className="flex gap-4 overflow-x-auto pb-4 hide-scrollbar -mx-4 px-4 md:-mx-8 md:px-8 lg:-mx-12 lg:px-12">
              {continueWatching.map((series) => (
                <ContinueWatchingCard key={series.id} series={series} />
              ))}
            </div>
          </section>
        )}

        {/* Personalized Recommendations */}
        {isAuthenticated && recommendations.length > 0 && (
          <MediaRow 
            title="Aanbevolen voor jou" 
            items={recommendations} 
            icon={Sparkles}
            userMovies={userMovies}
            onQuickAction={handleQuickAction}
          />
        )}

        <MediaRow 
          title="Trending Films" 
          items={trendingMovies} 
          icon={TrendingUp}
          viewAllLink="/discover/movie"
          userMovies={userMovies}
          onQuickAction={handleQuickAction}
        />
        
        <MediaRow 
          title="Trending Series" 
          items={trendingTv} 
          icon={TrendingUp}
          viewAllLink="/discover/tv"
          userMovies={userMovies}
          onQuickAction={handleQuickAction}
        />
      </div>

      {/* CTA for non-authenticated users */}
      {!isAuthenticated && (
        <section className="px-4 md:px-8 lg:px-12 py-16">
          <div className="glass-card p-8 md:p-12 text-center max-w-2xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-display font-bold mb-4">
              Begin met het bijhouden van je kijkgedrag
            </h2>
            <p className="text-gray-400 mb-8">
              Houd bij welke films en series je hebt gekeken, ontdek nieuwe content en bekijk je statistieken.
            </p>
            <Link to="/login">
              <Button 
                data-testid="cta-login-btn"
                className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-full px-8 py-6 font-semibold shadow-[0_0_20px_rgba(99,102,241,0.4)]"
              >
                Start Nu
              </Button>
            </Link>
          </div>
        </section>
      )}
    </div>
  );
};

export default HomePage;
